import { createClient } from '@supabase/supabase-js'
import { buildVerificationLink, uniqStrings } from './utils'
import { clearPersistedAuthArtifacts } from './authFlow'
import { isPartnerScopedRole } from './authorization'

const DEFAULT_SUPABASE_URL = 'https://ewqzogwiqldmrdfghznn.supabase.co'
const DEFAULT_SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV3cXpvZ3dpcWxkbXJkZmdoem5uIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzIzMDM5MDYsImV4cCI6MjA4Nzg3OTkwNn0.Lt4blCL36_xYGfshO6sSGHh5TZAcCs__8L2rTyBuxbg'

let url = import.meta.env.VITE_SUPABASE_URL
let key = import.meta.env.VITE_SUPABASE_ANON_KEY

try {
  const host = typeof window !== 'undefined' ? String(window.location?.hostname || '') : ''
  if (host.endsWith('sofera.com.br') || host === 'localhost' || host === '127.0.0.1') {
    url = DEFAULT_SUPABASE_URL
    key = DEFAULT_SUPABASE_ANON_KEY
  }
} catch {}

url = url || DEFAULT_SUPABASE_URL
key = key || DEFAULT_SUPABASE_ANON_KEY

export const SUPABASE_URL = url
export const SUPABASE_ANON_KEY = key

// O lock padrão do supabase-js usa `navigator.locks` com a opção `steal`, que
// aborta a operação que estava em andamento com "AbortError: Lock broken by
// another request with the 'steal' option.". Com o React em StrictMode (e com
// várias chamadas de auth no boot) isso derrubava a restauração da sessão e o
// usuário caía na tela de login. Aqui as operações de auth são serializadas em
// memória: continuam na ordem, mas nunca são abortadas.
const serializedAuthLock = (() => {
  let chain = Promise.resolve()
  return (_name, _acquireTimeout, fn) => {
    const run = chain.then(() => fn())
    chain = run.then(() => undefined, () => undefined)
    return run
  }
})()

export const supabase = createClient(url, key, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    lock: serializedAuthLock,
  }
})

// Cliente dedicado SOMENTE ao envio do e-mail de redefinição de senha.
// Usa o fluxo "implicit" de propósito: o link precisa funcionar em qualquer
// navegador/dispositivo. No fluxo PKCE o link carrega apenas um `code` que só
// pode ser trocado no MESMO navegador que solicitou a redefinição (o
// code_verifier fica salvo localmente), o que quebra o reset quando o usuário
// abre o e-mail em outro navegador (ex.: app de e-mail no celular).
export const supabasePasswordReset = createClient(url, key, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
    flowType: 'implicit',
    lock: serializedAuthLock,
  }
})

const decodeJwtPayload = (token) => {
  try {
    const parts = String(token || '').split('.')
    if (parts.length < 2) return null
    const payloadB64 = parts[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(parts[1].length / 4) * 4, '=')
    const json = atob(payloadB64)
    return JSON.parse(json)
  } catch {
    return null
  }
}

const expectedRefFromUrl = (() => {
  try {
    const host = String(url || '').replace(/^https?:\/\//, '').split('/')[0]
    const ref = host.split('.')[0]
    return ref || null
  } catch {
    return null
  }
})()

;(async () => {
  try {
    const { data } = await supabase.auth.getSession()
    const accessToken = data?.session?.access_token || ''
    if (!accessToken || !expectedRefFromUrl) return
    const payload = decodeJwtPayload(accessToken)
    const ref =
      String(payload?.ref || '') ||
      String(payload?.iss || '').replace(/^https?:\/\//, '').split('.')[0]
    if (ref && ref !== expectedRefFromUrl) {
      clearPersistedAuthArtifacts()
      await supabase.auth.signOut()
    }
  } catch {}
})()

export async function uploadPublic(path, file) {
  const bucket = 'communications'
  const { error } = await supabase.storage.from(bucket).upload(path, file, { upsert: true, contentType: file.type })
  if (error) throw error
  const { data } = supabase.storage.from(bucket).getPublicUrl(path)
  return data.publicUrl
}

export async function listCertificateTemplates() {
  const tryOrder = async (select, key) => {
    try {
      const { data, error } = await supabase
        .from('certificate_templates')
        .select(select)
        .order(key, { ascending: false })
      if (error) throw error
      return data || []
    } catch (e) {
      return null
    }
  }
  // Prefer full payload (includes meta and back_image_path) to support HTML-verso/sections
  const orders = ['id']
  let rows = null;
  for (const key of orders) {
    const full = await tryOrder('*', key)
    if (full) { rows = full; break; }
    // Fallback with explicit columns including meta when wildcard fails
    const withMeta = await tryOrder(
      'id,name,description,html,variables,front_image_path,back_image_path,meta,active,use_html,is_default,sas_partner_id',
      key
    )
    if (withMeta) { rows = withMeta; break; }
    // Last-resort minimal selection
    const minimal = await tryOrder(
      'id,name,description,html,variables,front_image_path,back_image_path,active,use_html,is_default,sas_partner_id',
      key
    )
    if (minimal) { rows = minimal; break; }
  }
  
  if (!rows) {
    const { data, error } = await supabase.from('certificate_templates').select('*')
    if (error) throw error
    rows = data || []
  }

  // Fetch linked partners for all templates
  if (rows.length > 0) {
    const { data: partners } = await supabase
      .from('certificate_template_partners')
      .select('template_id, sas_partner_id')
      .in('template_id', rows.map(r => r.id))
    
    if (partners) {
      rows.forEach(r => {
        r.sas_partner_ids = partners.filter(p => p.template_id === r.id).map(p => p.sas_partner_id)
      })
    }
  }

  return rows
}

export async function getSignedUrl(bucket, path, expiresIn = 3600) {
  if (!path) return null
  const { data, error } = await supabase
    .storage
    .from(bucket)
    .createSignedUrl(path, expiresIn)
  if (error) return null
  return data?.signedUrl || null
}

export async function uploadTemplateImage(templateId, side, file) {
  if (!file || !templateId) return null
  const ext = (file.type && file.type.split('/')[1]) || 'png'
  const path = `${templateId}/${side}.${ext}`
  const contentType = file.type || 'application/octet-stream'
  const { error } = await supabase.storage.from('certificate-templates').upload(path, file, { upsert: true, contentType })
  if (error) throw error
  return path
}

export async function upsertCertificateTemplate(model) {
  const payload = {
    name: model.name,
    html: model.customTextFront || '',
    variables: uniqStrings([...(model.frontFields || []), ...(model.backFields || [])].map(f => (typeof f === 'string' ? f : f?.name))),
    use_html: model.useHtml
  }

  if (typeof model.active === 'boolean') {
    payload.active = model.active
  } else if (typeof model.status === 'string') {
    payload.active = model.status === 'Ativo'
  }

  const hasIsDefault = typeof model.isDefault === 'boolean'
  const hasSasPartnerIds = Array.isArray(model.sasPartnerIds)
  if (hasIsDefault) payload.is_default = !!model.isDefault
  
  // Limpa a coluna legada de parceiro único
  payload.sas_partner_id = null

  // Rule: if default, it cannot have specific partners
  if (payload.is_default) {
    model.sasPartnerIds = []
  }

  const backSections = {
    header_html: model.backHeaderHtml || '',
    main_html: model.backMainHtml || '',
    institutional_html: model.backInstitutionalHtml || '',
    declaration_html: model.backDeclarationHtml || '',
    show_qr_code: !!model.backShowQrCode
  }

  const layoutBack = {
    lineHeight: typeof model.lineHeightBack === 'number' ? model.lineHeightBack : 1.6,
    x: typeof model.xBack === 'number' ? model.xBack : 15,
    y: typeof model.yBack === 'number' ? model.yBack : 18,
    width: typeof model.widthBack === 'number' ? model.widthBack : 70,
    qr: {
      x: typeof model.backQrX === 'number' ? model.backQrX : 85,
      y: typeof model.backQrY === 'number' ? model.backQrY : 50,
    },
    declaration: {
      x: typeof model.backDeclarationX === 'number' ? model.backDeclarationX : 50,
      y: typeof model.backDeclarationY === 'number' ? model.backDeclarationY : 85,
      width: typeof model.backDeclarationWidth === 'number'
        ? model.backDeclarationWidth
        : (typeof model.widthBack === 'number' ? model.widthBack : 70)
    }
  }
  let backHtml = [
    '<div data-back="true" style="position:relative; width:100%; height:100%;">',
    '<div data-back-content="true">',
    backSections.header_html || '',
    backSections.main_html || '',
    backSections.institutional_html || '',
    '</div>',
    backSections.show_qr_code
      ? `<div data-qr="true" style="position:absolute; left:${layoutBack.qr.x}%; top:${layoutBack.qr.y}%; transform:translate(-50%,-50%); width:190px; text-align:center;"><div style="font-size:11px; letter-spacing:.08em; text-transform:uppercase;">Validação</div><div style="margin-top:4px; font-family:monospace; font-size:12px; font-weight:700;">{{codigo_verificacao}}</div><img alt="QR Code" style="width:150px;height:150px;margin:10px auto 0; display:block;" src="https://api.qrserver.com/v1/create-qr-code/?size=220x220&data={{qrcode_url}}"/></div>`
      : '',
    `<div data-averbacao="true" style="position:absolute; left:${layoutBack.declaration.x}%; top:${layoutBack.declaration.y}%; width:${layoutBack.declaration.width}%; transform:translate(-50%,-50%); border:1px solid #111; min-height:90px; padding:12px;">`,
    backSections.declaration_html || '',
    '</div>',
    '</div>'
  ].join('')

  if (model.useHtml && model.customTextBack) {
    backHtml = model.customTextBack
  }

  payload.meta = {
    use_html: model.useHtml,
    front_fields: (model.frontFields||[]),
    back_fields: (model.backFields||[]),
    back_sections: backSections,
    back_qr_enabled: backSections.show_qr_code,
    back_html: backHtml,
    layout_front: {
      lineHeight: typeof model.lineHeightFront === 'number' ? model.lineHeightFront : 1.6,
      x: typeof model.xFront === 'number' ? model.xFront : 15,
      y: typeof model.yFront === 'number' ? model.yFront : 18,
      width: typeof model.widthFront === 'number' ? model.widthFront : 70
    },
    layout_back: layoutBack
  }

  let row
  if (model.id) {
    const { data, error } = await supabase
      .from('certificate_templates')
      .update(payload)
      .eq('id', model.id)
      .select('*')
      .single()
    if (error) throw error
    row = data
  } else {
    const { data, error } = await supabase
      .from('certificate_templates')
      .insert(payload)
      .select('*')
      .single()
    if (error) throw error
    row = data
  }

  // Update linked partners if provided
  if (hasSasPartnerIds) {
    // Delete old links
    await supabase.from('certificate_template_partners').delete().eq('template_id', row.id)
    
    // Insert new links
    if (model.sasPartnerIds.length > 0) {
      const links = model.sasPartnerIds.map(pid => ({
        template_id: row.id,
        sas_partner_id: pid
      }))
      const { error: linkErr } = await supabase.from('certificate_template_partners').insert(links)
      if (linkErr) throw linkErr
    }
    row.sas_partner_ids = model.sasPartnerIds
  }

  // Upload images if provided
  if (model.frontFile) {
    const p = await uploadTemplateImage(row.id, 'front', model.frontFile)
    const { error } = await supabase.from('certificate_templates').update({ front_image_path: p }).eq('id', row.id)
    if (error) throw error
    row.front_image_path = p
  }
  if (model.backFile) {
    const p = await uploadTemplateImage(row.id, 'back', model.backFile)
    const { error } = await supabase.from('certificate_templates').update({ back_image_path: p }).eq('id', row.id)
    if (error) throw error
    row.back_image_path = p
  }

  return row
}

export async function listCertificates(scope = {}) {
  const roleName = scope?.profileRole || null
  const partnerId = scope?.partnerId || scope?.profile?.sasPartnerId || null
  const myCompanyId = scope?.profile?.companyId || null
  const myCompanyIds = Array.from(new Set([
    ...(Array.isArray(scope?.profile?.companyIds) ? scope.profile.companyIds : []),
    ...(myCompanyId ? [myCompanyId] : []),
  ].filter(Boolean).map((id) => String(id))))

  const normalizeEmail = (v) => String(v || '').trim().toLowerCase()
  const normalizeDigits = (v) => String(v || '').replace(/\D/g, '')

  if (roleName === 'Ponto Focal') {
    const { data: scopedRows, error: scopedErr } = await supabase.rpc('list_certificates_for_my_company')
    if (!scopedErr && Array.isArray(scopedRows)) {
      return scopedRows.filter((cert) => String(cert?.status || '') !== 'revoked')
    }
  }

  const { data, error } = await supabase
    .from('certificates')
    .select(`*`)
    .order('issued_at', { ascending: false })
  
  if (error) throw error

  if (roleName === 'Ponto Focal' && myCompanyIds.length > 0) {
    const { data: usersInCompany, error: usersErr } = await supabase.rpc('admin_list_users_by_scope', {
      p_company_id: null,
      p_partner_id: null,
    })

    if (!usersErr && Array.isArray(usersInCompany)) {
      const allowedEmails = new Set(usersInCompany.map((u) => normalizeEmail(u?.email)))
      const allowedCpfs = new Set(usersInCompany.map((u) => normalizeDigits(u?.cpf)).filter(Boolean))
      const usersByEmail = new Map(usersInCompany.map((u) => [normalizeEmail(u?.email), u]))
      const usersByCpf = new Map(usersInCompany.map((u) => [normalizeDigits(u?.cpf), u]))

      return (data || [])
        .filter((cert) => {
          if (String(cert?.status || '') === 'revoked') return false
          const emailOk = allowedEmails.has(normalizeEmail(cert?.recipient_email))
          const cpfOk = cert?.recipient_document && allowedCpfs.has(normalizeDigits(cert.recipient_document))
          return Boolean(emailOk || cpfOk)
        })
        .map((cert) => ({
          ...(usersByEmail.get(normalizeEmail(cert?.recipient_email)) || usersByCpf.get(normalizeDigits(cert?.recipient_document)) || {}),
          ...cert,
          resolved_profile_id:
            usersByEmail.get(normalizeEmail(cert?.recipient_email))?.id ||
            usersByCpf.get(normalizeDigits(cert?.recipient_document))?.id ||
            null,
          company_id:
            cert.company_id ||
            usersByEmail.get(normalizeEmail(cert?.recipient_email))?.company_id ||
            usersByCpf.get(normalizeDigits(cert?.recipient_document))?.company_id ||
            null,
          company_name:
            cert.company_name ||
            usersByEmail.get(normalizeEmail(cert?.recipient_email))?.company_name ||
            usersByEmail.get(normalizeEmail(cert?.recipient_email))?.companies?.name ||
            usersByCpf.get(normalizeDigits(cert?.recipient_document))?.company_name ||
            usersByCpf.get(normalizeDigits(cert?.recipient_document))?.companies?.name ||
            null,
        }))
    }
  }

  // Buscar os perfis manualmente pelo e-mail
  const emailRaw = (data || []).filter(c => c.recipient_email).map(c => String(c.recipient_email).trim())
  const emailNormalized = emailRaw.map(normalizeEmail)
  const emails = [...new Set([...emailRaw, ...emailNormalized].filter(Boolean))]

  const certificateDocuments = [
    ...new Set(
      (data || [])
        .map((c) => normalizeDigits(c?.recipient_document))
        .filter(Boolean)
    ),
  ]

  const [profilesByEmailRes, profilesByDocumentRes] = await Promise.all([
    emails.length > 0
      ? supabase
          .from('profiles')
          .select('id, email, cpf, company_id, sas_partner_id, companies:companies!profiles_company_id_fkey(name), sas_partners(name)')
          .in('email', emails)
      : Promise.resolve({ data: [], error: null }),
    certificateDocuments.length > 0
      ? supabase
          .from('profiles')
          .select('id, email, cpf, company_id, sas_partner_id, companies:companies!profiles_company_id_fkey(name), sas_partners(name)')
          .in('cpf', certificateDocuments)
      : Promise.resolve({ data: [], error: null }),
  ])

  if (profilesByEmailRes.error) throw profilesByEmailRes.error
  if (profilesByDocumentRes.error) throw profilesByDocumentRes.error

  const profilesByEmail = {}
  const profilesByDocument = {}
  const mergedProfiles = new Map()

  ;[...(profilesByEmailRes.data || []), ...(profilesByDocumentRes.data || [])].forEach((profile) => {
    if (!profile?.id) return
    mergedProfiles.set(String(profile.id), {
      ...(mergedProfiles.get(String(profile.id)) || {}),
      ...profile,
    })
  })

  mergedProfiles.forEach((profile) => {
    const emailKey = normalizeEmail(profile?.email)
    const cpfKey = normalizeDigits(profile?.cpf)
    if (emailKey) profilesByEmail[emailKey] = profile
    if (cpfKey) profilesByDocument[cpfKey] = profile
  })

  const classCompanyByStudentCourse = new Map()
  const resolvedProfileIds = [...mergedProfiles.keys()]
  const resolvedCourseIds = [
    ...new Set(
      (data || [])
        .map((cert) => cert?.course_id)
        .filter(Boolean)
        .map((id) => String(id))
    ),
  ]

  if (resolvedProfileIds.length > 0 && resolvedCourseIds.length > 0) {
    try {
      const { data: classMatches, error: classMatchesErr } = await supabase
        .from('classes')
        .select('company_id, class_students!inner(student_id), class_courses!inner(course_id)')
        .not('company_id', 'is', null)
        .in('class_students.student_id', resolvedProfileIds)
        .in('class_courses.course_id', resolvedCourseIds)

      if (classMatchesErr) throw classMatchesErr

      const classCompanyIds = [
        ...new Set((classMatches || []).map((row) => row?.company_id).filter(Boolean).map((id) => String(id))),
      ]

      const companiesById = {}
      if (classCompanyIds.length > 0) {
        const { data: classCompanies, error: classCompaniesErr } = await supabase
          .from('companies')
          .select('id, name')
          .in('id', classCompanyIds)

        if (classCompaniesErr) throw classCompaniesErr

        ;(classCompanies || []).forEach((company) => {
          if (!company?.id) return
          companiesById[String(company.id)] = company.name || null
        })
      }

      ;(classMatches || []).forEach((row) => {
        const companyId = row?.company_id ? String(row.company_id) : null
        if (!companyId) return

        const companyName = companiesById[companyId] || null
        const studentRows = Array.isArray(row?.class_students) ? row.class_students : []
        const courseRows = Array.isArray(row?.class_courses) ? row.class_courses : []

        studentRows.forEach((studentRow) => {
          const studentId = studentRow?.student_id ? String(studentRow.student_id) : null
          if (!studentId) return

          courseRows.forEach((courseRow) => {
            const courseId = courseRow?.course_id ? String(courseRow.course_id) : null
            if (!courseId) return

            const key = `${studentId}::${courseId}`
            if (!classCompanyByStudentCourse.has(key)) {
              classCompanyByStudentCourse.set(key, { company_id: companyId, company_name: companyName })
            }
          })
        })
      })
    } catch (error) {
    }
  }

  let allowedCompanyIds = null;
  if (isPartnerScopedRole(roleName) && partnerId) {
    const [profilesScopeRes, classesScopeRes] = await Promise.all([
      supabase
        .from('profiles')
        .select('company_id')
        .eq('sas_partner_id', partnerId)
        .not('company_id', 'is', null),
      supabase
        .from('classes')
        .select('company_id')
        .eq('partner_id', partnerId)
        .not('company_id', 'is', null),
    ]);

    if (profilesScopeRes.error) throw profilesScopeRes.error;
    if (classesScopeRes.error) throw classesScopeRes.error;

    const companyIdsFromProfiles = (profilesScopeRes.data || []).map((row) => row.company_id).filter(Boolean);
    const companyIdsFromClasses = (classesScopeRes.data || []).map((row) => row.company_id).filter(Boolean);
    allowedCompanyIds = new Set(
      [...companyIdsFromProfiles, ...companyIdsFromClasses].map((id) => String(id))
    );
  }
  
  // Mapear os nomes das empresas e parceiros vindo dos perfis, se existirem
  const mappedData = data?.map(cert => {
    let company_name = cert.company_name || null;
    let partner_name = cert.partner_name || null;
    let company_id = cert.company_id || null;
    let sas_partner_id = cert.sas_partner_id || null;

    const emailKey = normalizeEmail(cert.recipient_email)
    const documentKey = normalizeDigits(cert.recipient_document)
    const profileByEmail = emailKey ? profilesByEmail[emailKey] : null
    const profileByDocument = documentKey ? profilesByDocument[documentKey] : null
    const profileId = profileByEmail?.id || profileByDocument?.id || null

    // Fallback para buscar direto dos perfis vinculados ao e-mail/CPF
    if (profileByEmail || profileByDocument) {
       const profileCandidates = [profileByEmail, profileByDocument].filter(Boolean)
       profileCandidates.forEach((profile) => {
         if (!company_id) company_id = profile.company_id || null
         if (!sas_partner_id) sas_partner_id = profile.sas_partner_id || null
         if (!company_name && profile.companies?.name) company_name = profile.companies.name
         if (!partner_name && profile.sas_partners?.name) partner_name = profile.sas_partners.name
       })
    }

    if ((!company_id || !company_name) && profileId && cert?.course_id) {
      const classCompany = classCompanyByStudentCourse.get(`${String(profileId)}::${String(cert.course_id)}`)
      if (classCompany) {
        if (!company_id) company_id = classCompany.company_id || null
        if (!company_name) company_name = classCompany.company_name || null
      }
    }
    
    return {
      ...cert,
      resolved_profile_id: profileId || null,
      company_name,
      partner_name,
      company_id,
      sas_partner_id,
    }
  });

  const scopedData = (mappedData || []).filter((cert) => {
    if (isPartnerScopedRole(roleName) && partnerId) {
      const certPartnerId = cert?.sas_partner_id ? String(cert.sas_partner_id) : null;
      const certCompanyId = cert?.company_id ? String(cert.company_id) : null;
      const belongsByPartner = certPartnerId === String(partnerId);
      const belongsByCompany = certCompanyId && allowedCompanyIds?.has(certCompanyId);
      return Boolean(belongsByPartner || belongsByCompany);
    }

    if (roleName === 'Ponto Focal' && myCompanyIds.length > 0) {
      if (String(cert?.status || '') === 'revoked') return false
      return myCompanyIds.includes(String(cert?.company_id || ''));
    }

    return true;
  });
  
  return scopedData
}

export async function deleteCertificateTemplate(id) {
  const { error } = await supabase
    .from('certificate_templates')
    .delete()
    .eq('id', id)
  if (error) throw error
  return true
}

export async function verifyCertificatePublic(code) {
  const { data, error } = await supabase.rpc('verify_certificate', { p_code: code })
  if (error) throw error
  return data || []
}

export { buildVerificationLink }

export async function deleteCertificate(id) {
  const { error } = await supabase.from('certificates').delete().eq('id', id)
  if (error) throw error
  return true
}

export async function updateCertificateStatus(id, status) {
  const { data, error } = await supabase
    .from('certificates')
    .update({ status })
    .eq('id', id)
    .select('*')
    .single()
  if (error) throw error
  return data
}

export async function updateCertificate({ id, templateId, vars, extra }) {
  // Fetch template html
  const { data: template, error: tErr } = await supabase.from('certificate_templates').select('*').eq('id', templateId).single()
  if (tErr) throw tErr
  const { data: rendered, error: rErr } = await supabase.rpc('render_template_html', { template_html: template.html || '', vars })
  if (rErr) throw rErr
  const lf = template.meta?.layout_front || {}
  const lh = (typeof lf.lineHeight === 'number') ? lf.lineHeight : 1.6
  const x = (typeof lf.x === 'number') ? lf.x : 15
  const y = (typeof lf.y === 'number') ? lf.y : 18
  const w = (typeof lf.width === 'number') ? lf.width : 70
  const withLayout = `<div style="position:relative; width:${w}%; margin-left:${x}%; margin-top:${y}%; line-height:${lh};">${rendered || ''}</div>`

  const payload = {
    template_id: templateId,
    course_id: extra?.course_id ?? null,
    company_id: extra?.company_id ?? null,
    company_name: vars.empresa || vars['Empresa'] || null,
    sas_partner_id: extra?.sas_partner_id ?? null,
    recipient_name: vars.aluno || vars['Nome do Aluno'] || '',
    recipient_email: vars.email || vars['E-mail'] || '',
    course_title: vars.curso || vars['Nome do Curso'] || '',
    workload_hours: extra?.workload_hours ?? null,
    final_grade: vars.nota_final || vars['Nota Final'] || '',
    start_date: extra?.start_date ?? null,
    end_date: extra?.end_date ?? null,
    instructor_name: vars.instrutor || vars['Instrutor'] || '',
    coordinator_name: vars.coordenador || vars['Coordenador'] || '',
    partner_name: vars.parceiro_sas || vars['Parceiro SAS'] || '',
    program_content: vars.conteudo_programatico || vars['Conteúdo Programático'] || '',
    html: withLayout,
    status: extra?.status || 'issued',
    manual_issue: extra?.manual_issue === true,
  }
  
  const { data, error } = await supabase.from('certificates').update(payload).eq('id', id).select('*').single()
  if (error) throw error
  
  const code = data.validation_code
  const hashCode = data.hash_code
  const base = (typeof window !== 'undefined' && window.location?.origin) || ''
  const verifyUrl = `${base}/validar?code=${encodeURIComponent(code)}`
  
  // Atualiza as vars com o hash_code real do banco antes de renderizar
  const updatedVars = { ...vars, codigo_verificacao: code, hash_code: hashCode, qrcode_url: verifyUrl };
  
  const { data: finalHtml, error: renderErr } = await supabase.rpc('render_template_html', { template_html: withLayout || '', vars: updatedVars })
  if (renderErr) throw renderErr

  const ensureQrSection = (html, codeVal, urlVal) => {
    try {
      const h = html || ''
      const hasQr = /qrcode_url|qrserver|qr\s*code/i.test(h)
      if (hasQr) return h
      const safeUrlVal = urlVal.replace('&action=download', '').replace('?action=download', '')
      const snippet = `\n<div style="margin-top:24px;padding:16px;border:1px dashed #cbd5e1;border-radius:8px;font-family:Arial,Helvetica,sans-serif;">\n  <p style="margin:0 0 8px 0;color:#334155;font-size:12px;letter-spacing:.06em;text-transform:uppercase">Verificação</p>\n  <div style="display:flex;align-items:center;gap:16px">\n    <img alt="QR Code" src="https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(safeUrlVal)}" style="width:110px;height:110px"/>\n    <div style="font-size:12px;color:#334155">\n      <p style="margin:0 0 4px 0">Código: <strong>${codeVal}</strong></p>\n      <a href="${safeUrlVal}" style="color:#0ea5e9;text-decoration:none" target="_blank" rel="noopener">${safeUrlVal}</a>\n    </div>\n  </div>\n</div>`
      if (/(<\/body>)/i.test(h)) return h.replace(/(<\/body>)/i, `${snippet}$1`)
      return h + snippet;
    } catch { return html }
  }

  const processed = ensureQrSection(finalHtml || withLayout || '', code, verifyUrl)
  await supabase.from('certificates').update({ html: processed }).eq('id', data.id)
  return { ...data, html: processed }
}

export async function issueCertificate({ templateId, vars, extra }) {
  // Fetch template html
  const { data: template, error: tErr } = await supabase.from('certificate_templates').select('*').eq('id', templateId).single()
  if (tErr) throw tErr
  const { data: rendered, error: rErr } = await supabase.rpc('render_template_html', { template_html: template.html || '', vars })
  if (rErr) throw rErr
  const lf = template.meta?.layout_front || {}
  const lh = (typeof lf.lineHeight === 'number') ? lf.lineHeight : 1.6
  const x = (typeof lf.x === 'number') ? lf.x : 15
  const y = (typeof lf.y === 'number') ? lf.y : 18
  const w = (typeof lf.width === 'number') ? lf.width : 70
  const withLayout = `<div style="position:relative; width:${w}%; margin-left:${x}%; margin-top:${y}%; line-height:${lh};">${rendered || ''}</div>`
  const payload = {
    template_id: templateId,
    course_id: extra?.course_id ?? null,
    company_id: extra?.company_id ?? null,
    company_name: vars.empresa || vars['Empresa'] || null,
    sas_partner_id: extra?.sas_partner_id ?? null,
    recipient_name: vars.aluno || vars['Nome do Aluno'] || '',
    recipient_email: vars.email || vars['E-mail'] || '',
    course_title: vars.curso || vars['Nome do Curso'] || '',
    workload_hours: extra?.workload_hours ?? null,
    final_grade: vars.nota_final || vars['Nota Final'] || '',
    start_date: extra?.start_date ?? null,
    end_date: extra?.end_date ?? null,
    instructor_name: vars.instrutor || vars['Instrutor'] || '',
    coordinator_name: vars.coordenador || vars['Coordenador'] || '',
    partner_name: vars.parceiro_sas || vars['Parceiro SAS'] || '',
    program_content: vars.conteudo_programatico || vars['Conteúdo Programático'] || '',
    html: withLayout,
    status: extra?.status || 'issued',
    manual_issue: extra?.manual_issue === true,
  }
  const { data, error } = await supabase.from('certificates').insert(payload).select('*').single()
  if (error) throw error
  const code = data.validation_code
  const hashCode = data.hash_code
  const base = (typeof window !== 'undefined' && window.location?.origin) || ''
  const verifyUrl = `${base}/validar?code=${encodeURIComponent(code)}`
  
  // Atualiza as vars com o hash_code real do banco antes de renderizar
  const updatedVars = { ...vars, codigo_verificacao: code, hash_code: hashCode, qrcode_url: verifyUrl };
  
  const { data: finalHtml, error: renderErr } = await supabase.rpc('render_template_html', { template_html: withLayout || '', vars: updatedVars })
  if (renderErr) throw renderErr

  const ensureQrSection = (html, codeVal, urlVal) => {
    try {
      const h = html || ''
      const hasQr = /qrcode_url|qrserver|qr\s*code/i.test(h)
      if (hasQr) return h
      const safeUrlVal = urlVal.replace('&action=download', '').replace('?action=download', '')
      const snippet = `\n<div style="margin-top:24px;padding:16px;border:1px dashed #cbd5e1;border-radius:8px;font-family:Arial,Helvetica,sans-serif;">\n  <p style="margin:0 0 8px 0;color:#334155;font-size:12px;letter-spacing:.06em;text-transform:uppercase">Verificação</p>\n  <div style="display:flex;align-items:center;gap:16px">\n    <img alt="QR Code" src="https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(safeUrlVal)}" style="width:110px;height:110px"/>\n    <div style="font-size:12px;color:#334155">\n      <p style="margin:0 0 4px 0">Código: <strong>${codeVal}</strong></p>\n      <a href="${safeUrlVal}" style="color:#0ea5e9;text-decoration:none" target="_blank" rel="noopener">${safeUrlVal}</a>\n    </div>\n  </div>\n</div>`
      if (/(<\/body>)/i.test(h)) return h.replace(/(<\/body>)/i, `${snippet}$1`)
      return h + snippet;
    } catch { return html }
  }

  const processed = ensureQrSection(finalHtml || withLayout || '', code, verifyUrl)
  await supabase.from('certificates').update({ html: processed }).eq('id', data.id)
  return { ...data, html: processed }
}
